import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { fmt, FOLLOWUP_LABEL, STAGE_LABEL } from '@/lib/format';
import { useSearch } from '@/lib/queries';
import type { PipelineStage } from '@performance-crm/shared';

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const { data, isFetching } = useSearch(debounced);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (open) {
      setQ('');
      setDebounced('');
    }
  }, [open]);

  const go = (leadId: string) => {
    onOpenChange(false);
    navigate(`/leads/${leadId}`);
  };

  const hits = data?.hits ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-24 max-w-xl translate-y-0 p-0">
        <DialogTitle className="sr-only">Búsqueda global</DialogTitle>
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="text-muted-foreground h-4 w-4" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hits[0]?.leads[0]) go(hits[0].leads[0].id);
            }}
            placeholder="Buscar por nombre, teléfono, correo o documento…"
            className="h-12 w-full bg-transparent text-sm outline-none"
            aria-label="Buscar"
          />
          {isFetching && <span className="text-muted-foreground text-xs">Buscando…</span>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {debounced.length < 2 && (
            <p className="text-muted-foreground p-3 text-sm">
              Escribe al menos 2 letras del nombre, o 4 dígitos del teléfono o documento. Atajo: ⌘K
              / Ctrl+K.
            </p>
          )}
          {debounced.length >= 2 && !isFetching && hits.length === 0 && (
            <p className="text-muted-foreground p-3 text-sm">Sin resultados para “{debounced}”.</p>
          )}
          {hits.map((h) => (
            <div key={h.contactId} className="hover:bg-accent rounded-md p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">{h.name}</p>
                <span className="text-muted-foreground text-xs">
                  {h.phone ?? h.email ?? h.documentNumber ?? ''} · {h.companyName}
                </span>
              </div>
              {h.leads.length === 0 && (
                <p className="text-muted-foreground text-xs">Sin leads visibles</p>
              )}
              <ul className="mt-1 space-y-1">
                {h.leads.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => go(l.id)}
                      className="hover:bg-primary/10 flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm"
                    >
                      <span>
                        {l.projectName}{' '}
                        <span className="text-muted-foreground">· {fmt.date(l.capturedAt)}</span>
                      </span>
                      <span className="flex gap-1">
                        <Badge tone="outline">
                          {STAGE_LABEL[l.pipelineStage as PipelineStage] ?? l.pipelineStage}
                        </Badge>
                        <Badge tone="muted">
                          {FOLLOWUP_LABEL[l.followupState] ?? l.followupState}
                        </Badge>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
